import { create } from 'zustand';
import { Agent, CreateAgentPayload, UpdateAgentPayload } from '@/lib/api/agent';
import * as agentApi from '@/lib/api/agent';
import { toast } from 'sonner';

interface AgentState {
  agents: Agent[];
  isAgentsLoading: boolean;
  editingAgentId: string | null;

  fetchAgents: (activeOnly?: boolean) => Promise<void>;
  fetchAgentForEditing: (id: string) => Promise<void>;
  createAgent: (payload: CreateAgentPayload) => Promise<Agent | null>;
  updateAgent: (id: string, payload: UpdateAgentPayload) => Promise<Agent | null>;
  deleteAgent: (id: string) => Promise<void>;
  duplicateAgent: (id: string) => Promise<Agent | null>;
  setAgentAsDefault: (id: string) => Promise<void>;
  setEditingAgentId: (id: string | null) => void;
  clearEditingAgentId: () => void;
}

export const useAgentStore = create<AgentState>((set) => ({
  agents: [],
  isAgentsLoading: false,
  editingAgentId: null,

  fetchAgents: async (activeOnly = false) => {
    set({ isAgentsLoading: true });
    try {
      const data = await agentApi.getAgents(activeOnly);
      set({ agents: data, isAgentsLoading: false });
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      set({ isAgentsLoading: false });
    }
  },

  fetchAgentForEditing: async (id: string) => {
    set({ isAgentsLoading: true });
    try {
      const data = await agentApi.getAgent(id);
      set((state) => ({
        agents: state.agents.map((agent) =>
          agent.id === id ? data : agent,
        ),
        editingAgentId: id,
        isAgentsLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch agent:', error);
      set({ isAgentsLoading: false });
    }
  },

  createAgent: async (payload: CreateAgentPayload) => {
    set({ isAgentsLoading: true });
    try {
      const newAgent = await agentApi.createAgent(payload);
      set((state) => ({
        agents: [...state.agents, newAgent],
        isAgentsLoading: false,
      }));
      toast.success(`Agent "${newAgent.name}" created successfully.`);
      return newAgent;
    } catch (error) {
      console.error('Failed to create agent:', error);
      set({ isAgentsLoading: false });
      toast.error('Failed to create agent.');
      return null;
    }
  },

  updateAgent: async (id: string, payload: UpdateAgentPayload) => {
    set({ isAgentsLoading: true });
    try {
      const updatedAgent = await agentApi.updateAgent(id, payload);
      set((state) => ({
        agents: state.agents.map((agent) =>
          agent.id === id ? updatedAgent : agent,
        ),
        isAgentsLoading: false,
      }));
      toast.success('Agent updated successfully.');
      return updatedAgent;
    } catch (error) {
      console.error('Failed to update agent:', error);
      set({ isAgentsLoading: false });
      toast.error('Failed to update agent.');
      return null;
    }
  },

  deleteAgent: async (id: string) => {
    set({ isAgentsLoading: true });
    try {
      await agentApi.deleteAgent(id);
      set((state) => ({
        agents: state.agents.filter((agent) => agent.id !== id),
        isAgentsLoading: false,
      }));
      toast.success('Agent deleted successfully.');
    } catch (error) {
      console.error('Failed to delete agent:', error);
      set({ isAgentsLoading: false });
      toast.error('Failed to delete agent.');
      throw error;
    }
  },

  duplicateAgent: async (id: string) => {
    set({ isAgentsLoading: true });
    try {
      const newAgent = await agentApi.duplicateAgent(id);
      set((state) => ({
        agents: [...state.agents, newAgent],
        isAgentsLoading: false,
      }));
      toast.success(`Agent duplicated as "${newAgent.name}".`);
      return newAgent;
    } catch (error) {
      console.error('Failed to duplicate agent:', error);
      set({ isAgentsLoading: false });
      toast.error('Failed to duplicate agent.');
      return null;
    }
  },

  setAgentAsDefault: async (id: string) => {
    try {
      const updatedAgent = await agentApi.setAgentAsDefault(id);
      // Update the is_default flag for all agents in the store
      set((state) => ({
        agents: state.agents.map((agent) => ({
          ...agent,
          is_default: agent.id === id,
        })),
      }));
      toast.success(`"${updatedAgent.name}" set as default agent.`);
    } catch (error) {
      console.error('Failed to set default agent:', error);
      toast.error('Failed to set default agent.');
    }
  },

  setEditingAgentId: (id: string | null) => {
    set({ editingAgentId: id });
  },

  clearEditingAgentId: () => {
    set({ editingAgentId: null });
  },
}));